/**
 * Preserve catalog/overview filters and scroll position when navigating
 * to a product page and back.
 *
 * Filters already live in the URL; this module stores the overview URL
 * plus scrollY in sessionStorage when a product card is clicked, then
 * restores scroll after returning (smooth scroll to the previous spot).
 */

const STORAGE_KEY = 'kerstpakketten.overviewReturn';
const TTL_MS = 60 * 60 * 1000;

/**
 * Normalize a URL to pathname + sorted search for reliable comparison.
 *
 * @param {string} url
 * @returns {string|null}
 */
function normalizeUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        const path = parsed.pathname.replace(/\/+$/, '') || '/';
        const entries = [...parsed.searchParams.entries()].sort((a, b) => {
            if (a[0] === b[0]) {
                return String(a[1]).localeCompare(String(b[1]));
            }

            return a[0].localeCompare(b[0]);
        });
        const params = new URLSearchParams(entries);
        const query = params.toString();

        return query ? `${path}?${query}` : path;
    } catch (_) {
        return null;
    }
}

/**
 * Whether the URL is a same-origin kerstpakketten overview (not a product detail).
 *
 * Product details end with /{numericId}. Legacy /product/{sku} paths are excluded.
 *
 * @param {string} url
 * @returns {boolean}
 */
function isOverviewUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);

        if (parsed.origin !== window.location.origin) {
            return false;
        }

        const path = parsed.pathname.replace(/\/+$/, '') || '/';

        if (path === '/kerstpakketten' || path.startsWith('/kerstpakketten/')) {
            if (/\/\d+$/.test(path)) {
                return false;
            }

            if (path === '/kerstpakketten/categorieen') {
                return false;
            }

            return true;
        }

        return false;
    } catch (_) {
        return false;
    }
}

/**
 * Whether the URL points to a product detail page.
 *
 * @param {string} url
 * @returns {boolean}
 */
function isProductDetailUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        const path = parsed.pathname.replace(/\/+$/, '') || '/';

        if (/^\/product\//.test(path)) {
            return true;
        }

        return path.startsWith('/kerstpakketten/') && /\/\d+$/.test(path);
    } catch (_) {
        return false;
    }
}

/**
 * Current vertical scroll offset.
 *
 * @returns {number}
 */
function currentScrollY() {
    return window.scrollY
        || window.pageYOffset
        || document.documentElement.scrollTop
        || document.body.scrollTop
        || 0;
}

/**
 * Height of the sticky site header, plus a small gap so content is not hidden.
 *
 * @returns {number}
 */
function getStickyHeaderOffset() {
    const header = document.querySelector('header.sticky');

    if (!header) {
        return 16;
    }

    return Math.ceil(header.getBoundingClientRect().height) + 16;
}

/**
 * Scroll the window to a Y position.
 *
 * @param {number} top
 * @param {ScrollBehavior} behavior
 * @returns {void}
 */
function scrollWindowTo(top, behavior) {
    const y = Math.max(0, top);

    // Direct scrollTop writes cancel CSS/JS smooth scrolling — only use them for instant jumps.
    if (behavior === 'smooth') {
        window.scrollTo({ top: y, left: 0, behavior: 'smooth' });

        return;
    }

    window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = y;
    document.body.scrollTop = y;
}

/**
 * Scroll so the element sits just below the sticky header.
 *
 * @param {Element} el
 * @param {ScrollBehavior} behavior
 * @returns {void}
 */
function scrollElementBelowHeader(el, behavior) {
    const top = el.getBoundingClientRect().top + currentScrollY() - getStickyHeaderOffset();

    scrollWindowTo(top, behavior);
}

/**
 * Read and validate the stored return payload.
 *
 * @returns {{ url: string, scrollY: number, at: number, restore?: boolean, productPath?: string }|null}
 */
function readStored() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);

        if (!raw) {
            return null;
        }

        const data = JSON.parse(raw);

        if (!data?.url || typeof data.scrollY !== 'number' || typeof data.at !== 'number') {
            sessionStorage.removeItem(STORAGE_KEY);

            return null;
        }

        if (Date.now() - data.at > TTL_MS) {
            sessionStorage.removeItem(STORAGE_KEY);

            return null;
        }

        if (!isOverviewUrl(data.url)) {
            sessionStorage.removeItem(STORAGE_KEY);

            return null;
        }

        return data;
    } catch (_) {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (__) {
            // sessionStorage may be unavailable
        }

        return null;
    }
}

/**
 * Write the return payload to sessionStorage.
 *
 * @param {{ url: string, scrollY: number, at: number, restore?: boolean, productPath?: string }} data
 * @returns {void}
 */
function writeStored(data) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (_) {
        // sessionStorage may be blocked
    }
}

/**
 * Persist the current overview URL and scroll position.
 *
 * @param {{ productPath?: string }} [options]
 * @returns {void}
 */
function save(options = {}) {
    if (!isOverviewUrl(window.location.href)) {
        return;
    }

    writeStored({
        url: window.location.href,
        scrollY: currentScrollY(),
        at: Date.now(),
        restore: false,
        productPath: options.productPath || undefined,
    });
}

/**
 * Return the stored overview URL if still valid.
 *
 * @returns {string|null}
 */
function getUrl() {
    const data = readStored();

    return data?.url ?? null;
}

/**
 * Clear the stored return state.
 *
 * @returns {void}
 */
function clear() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        // sessionStorage may be unavailable
    }
}

/**
 * Apply scroll position. Prefer the saved scrollY (exact previous view);
 * fall back to the clicked product card, offset for the sticky header.
 *
 * @param {{ scrollY: number, productPath?: string }} data
 * @param {{ smooth?: boolean }} [options]
 * @returns {void}
 */
function applyScroll(data, options = {}) {
    const smooth = options.smooth !== false;
    const behavior = smooth ? 'smooth' : 'auto';
    const savedY = Math.max(0, Number(data.scrollY) || 0);

    // Exact previous viewport position matches "where I left off" best.
    if (savedY > 0) {
        scrollWindowTo(savedY, behavior);

        return;
    }

    if (data.productPath) {
        try {
            const productPath = new URL(data.productPath, window.location.origin).pathname;
            const links = document.querySelectorAll('[data-overview-results] a[href]');

            for (const link of links) {
                const href = link.getAttribute('href');

                if (!href) {
                    continue;
                }

                let linkPath;

                try {
                    linkPath = new URL(href, window.location.origin).pathname;
                } catch (_) {
                    continue;
                }

                if (linkPath !== productPath) {
                    continue;
                }

                const card = link.closest('article') || link;

                scrollElementBelowHeader(card, behavior);

                return;
            }
        } catch (_) {
            // Fall through.
        }
    }

    scrollWindowTo(savedY, behavior);
}

/**
 * Restore scroll when returning to the overview from a product page.
 *
 * @returns {void}
 */
function restoreScrollIfNeeded() {
    const data = readStored();

    if (!data || !isOverviewUrl(window.location.href)) {
        return;
    }

    const urlMatches = normalizeUrl(window.location.href) === normalizeUrl(data.url);

    // `restore` is set by the product-page back button. URL match covers browser back.
    if (!data.restore && !urlMatches) {
        return;
    }

    clear();

    try {
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
    } catch (_) {
        // Ignore browsers that block this.
    }

    // Start at the top, then animate down to the previous spot for clearer feedback.
    window.scrollTo(0, 0);

    requestAnimationFrame(() => {
        window.setTimeout(() => {
            applyScroll(data, { smooth: true });
        }, 100);

        // Only correct after the smooth scroll can finish, and only if we landed off-target
        // (e.g. late image layout). Avoids cancelling the animation with an instant jump.
        window.setTimeout(() => {
            const targetY = Math.max(0, Number(data.scrollY) || 0);

            if (targetY > 0 && Math.abs(currentScrollY() - targetY) > 48) {
                applyScroll(data, { smooth: false });
            }
        }, 1000);
    });
}

/**
 * Bind click capture on an overview root so product navigations save return state.
 *
 * @param {ParentNode} root
 * @returns {void}
 */
function bindSaveOnProductClicks(root) {
    if (!root || typeof root.addEventListener !== 'function') {
        return;
    }

    if (root.dataset.overviewReturnBound === '1') {
        return;
    }

    root.dataset.overviewReturnBound = '1';

    root.addEventListener('click', (event) => {
        const link = event.target?.closest?.('a[href]');

        if (!link || event.defaultPrevented) {
            return;
        }

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
            return;
        }

        const href = link.getAttribute('href');

        if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
            return;
        }

        if (!isProductDetailUrl(href)) {
            return;
        }

        let productPath;

        try {
            productPath = new URL(href, window.location.origin).pathname;
        } catch (_) {
            productPath = href;
        }

        save({ productPath });
    });
}

/**
 * Navigate back to the stored overview URL, or to the provided fallback.
 *
 * @param {string} fallbackUrl
 * @returns {void}
 */
function navigateBack(fallbackUrl) {
    const data = readStored();
    const target = data?.url && isOverviewUrl(data.url) ? data.url : fallbackUrl;

    if (data) {
        writeStored({
            ...data,
            restore: true,
            at: Date.now(),
        });
    }

    window.location.assign(target);
}

/**
 * Restore scroll after a full load and after bfcache back-navigation.
 * Also wires overview product clicks and the product-page back link
 * without depending on Alpine boot order.
 *
 * @returns {void}
 */
function bootOverviewReturn() {
    const resultsRoot = document.querySelector('[data-overview-results]');

    if (resultsRoot) {
        bindSaveOnProductClicks(resultsRoot);
        restoreScrollIfNeeded();
    }

    document.querySelectorAll('[data-overview-back]').forEach((link) => {
        const storedUrl = getUrl();

        if (storedUrl) {
            link.setAttribute('href', storedUrl);
        }

        if (link.dataset.overviewBackBound === '1') {
            return;
        }

        link.dataset.overviewBackBound = '1';
        link.addEventListener('click', (event) => {
            event.preventDefault();

            const fallback = link.getAttribute('data-overview-back-fallback') || link.getAttribute('href');

            navigateBack(fallback);
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootOverviewReturn);
} else {
    bootOverviewReturn();
}

window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        bootOverviewReturn();
    }
});

window.overviewReturn = {
    save,
    getUrl,
    clear,
    restoreScrollIfNeeded,
    bindSaveOnProductClicks,
    navigateBack,
    isOverviewUrl,
    isProductDetailUrl,
};
